import React from 'react';
import { Paper, Tooltip, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from 'styled-components';
import { FlexLayout } from '@Styled/utilities';
import AssetUploadButton from './AssetUploadButton';

type Props = {
	title: string;
	hint?: string;
	value?: string;
	onUploaded: (uri: string) => void;
	/** Clears the slot so the portals fall back to their bundled default. */
	onClear?: () => void;
	/** Lock uploads for this slot to a cropper at this ratio (see AssetUploadButton). */
	cropRatio?: number;
};

const AssetSlotCard: React.FC<Props> = ({
	title,
	hint,
	value,
	onUploaded,
	onClear,
	cropRatio,
}) => {
	return (
		<SlotCard>
			<FlexLayout justifyContent="space-between" alignItems="flex-start">
				<HintColumn>
					<Typography variant="subtitle1">{title}</Typography>
					{hint && (
						<Typography variant="caption" color="textSecondary">
							{hint}
						</Typography>
					)}
				</HintColumn>
				<SlotActions alignItems="center">
					<AssetUploadButton
						label={value ? 'Replace' : 'Upload'}
						onUploaded={onUploaded}
						cropRatio={cropRatio}
					/>
					{/* Only offered once something is set — clearing an empty slot
					    is a no-op that still round-trips to the API. */}
					{onClear && value && (
						<Tooltip title="Clear — revert to the built-in default">
							<IconButton size="small" onClick={onClear}>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</SlotActions>
			</FlexLayout>
			<PreviewArea>
				{value ? (
					<PreviewImage src={value} alt={title} />
				) : (
					<Typography variant="body2" color="textSecondary">
						Not set
					</Typography>
				)}
			</PreviewArea>
		</SlotCard>
	);
};

export default AssetSlotCard;

const SlotCard = styled(Paper)`
	padding: 1rem 1.5rem;
`;

/** Lets the hint wrap instead of shoving the actions off the card. */
const HintColumn = styled.div`
	padding-right: 1rem;
`;

const SlotActions = styled(FlexLayout)`
	flex-shrink: 0;
	gap: 0.25rem;
`;

const PreviewArea = styled.div`
	margin-top: 0.75rem;
	min-height: 6rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	background-color: ${(props) => props.theme.palette.grey['100']};
	background-image: linear-gradient(45deg, #eee 25%, transparent 25%),
		linear-gradient(-45deg, #eee 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, #eee 75%),
		linear-gradient(-45deg, transparent 75%, #eee 75%);
	background-size: 16px 16px;
	background-position: 0 0, 0 8px, 8px -8px, -8px 0px;
`;

const PreviewImage = styled.img`
	max-width: 100%;
	max-height: 8rem;
	object-fit: contain;
	padding: 0.5rem;
`;
